import dayjs, { Dayjs } from "dayjs";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarEvent,
  ScheduleSummary,
} from "../../services/scheduling/Scheduling.types";
import {
  eventTimeLabel,
  eventsForDay,
  getScheduleColor,
} from "./calendar.utils";
import styles from "./ScheduleCalendar.module.css";

interface MonthGridProps {
  anchorDate: Dayjs;
  days: Dayjs[];
  events: CalendarEvent[];
  schedules: ScheduleSummary[];
  onDayClick: (day: Dayjs) => void;
  onEventClick: (event: CalendarEvent) => void;
}

interface EventStyle extends CSSProperties {
  "--event-color": string;
}

export default function MonthGrid({
  anchorDate,
  days,
  events,
  schedules,
  onDayClick,
  onEventClick,
}: MonthGridProps) {
  const { t } = useTranslation();
  const weekDays = days.slice(0, 7);

  return (
    <div className={styles.monthGrid}>
      {weekDays.map((day) => (
        <div key={day.format("dd")} className={styles.monthWeekDay}>
          {day.format("dddd")}
        </div>
      ))}

      {days.map((day) => {
        const dayEvents = eventsForDay(events, day).sort(
          (left, right) => dayjs(left.start).valueOf() - dayjs(right.start).valueOf(),
        );
        const visibleEvents = dayEvents.slice(0, 3);
        const remaining = dayEvents.length - visibleEvents.length;
        const outside = day.month() !== anchorDate.month();
        const today = day.isSame(dayjs(), "day");

        return (
          <div
            key={day.format("YYYY-MM-DD")}
            className={[
              styles.monthDay,
              outside ? styles.monthDayOutside : "",
              today ? styles.monthDayToday : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <button
              type="button"
              className={styles.monthDayNumber}
              aria-label={day.format("D MMMM YYYY")}
              aria-current={today ? "date" : undefined}
              onClick={() => onDayClick(day)}
            >
              {day.date()}
            </button>

            <div className={styles.monthEvents}>
              {visibleEvents.map((event) => {
                const style: EventStyle = {
                  "--event-color": getScheduleColor(
                    event.scheduleIds[0] ?? "default",
                    schedules,
                  ),
                };

                return (
                  <button
                    key={event.id}
                    type="button"
                    className={styles.monthEvent}
                    style={style}
                    title={`${event.title} · ${eventTimeLabel(event)}`}
                    onClick={() => onEventClick(event)}
                  >
                    <span>{dayjs(event.start).format("HH:mm")}</span>
                    <strong>{event.title}</strong>
                  </button>
                );
              })}
              {remaining > 0 && (
                <span className={styles.moreEvents}>
                  {t("calendar.moreEvents", { count: remaining })}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
